import { logger } from '../utils/logger';
import { supabase } from './supabaseService';

type ProxyAction = 'chat_completion' | 'test_connection' | 'list_models';

// Call the openai-proxy Edge Function. The proxy owns the API key, verifies
// the user's Supabase Auth session, applies per-user rate limits, and forwards
// to OpenAI. This is the ONLY path from the frontend to OpenAI.
async function callOpenAIProxy(
  action: ProxyAction,
  payload?: Record<string, unknown>
): Promise<any> {
  const { data, error } = await supabase.functions.invoke('openai-proxy', {
    body: { action, payload },
  });

  if (error) {
    // Extract the JSON body the Edge Function returned so callers get the
    // actual error message ("Rate limit exceeded", "OpenAI not configured", …)
    // instead of the generic FunctionsHttpError shape.
    const status = (error as any).context?.response?.status ?? (error as any).status;
    const bodyText = await (error as any).context?.response?.text?.().catch(() => null);
    let parsed: any = null;
    try { parsed = bodyText ? JSON.parse(bodyText) : null; } catch { /* ignore */ }

    const detail = parsed?.error?.message || parsed?.error || parsed?.message || (error as Error).message;
    const err = new Error(detail || 'openai-proxy request failed') as any;
    err.status = status;
    err.retryable = status === 429 || (status && status >= 500);
    throw err;
  }

  return data;
}

interface OpenAIConfig {
  apiKey: string;
  organizationId?: string;
  model: string;
  maxTokens: number;
  temperature: number;
}

interface ContactData {
  fullName: string;
  company: string;
  email: string;
  phone: string;
  role?: string;
  leadStatus: string;
  productInterest: string;
  notes?: string;
}

interface LeadData {
  fullName: string;
  company: string;
  email: string;
  phone?: string;
  role?: string;
  source: string;
  status: string;
  productInterest: string;
  score: number;
  owner: string;
  notes?: string;
  createdAt: Date;
  lastContactDate?: Date;
  nextFollowUp?: Date;
}

interface ActivityData {
  id: string;
  type: string;
  datetime: Date;
  description: string;
  contactId: string;
  owner: string;
}

interface LeadPriorityAnalysis {
  priority: 'alta' | 'media' | 'baja';
  reasoning: string;
}

interface OpenAIUsage {
  action: string;
  model: string;
  promptTokens: number;
  completionTokens: number;
  totalTokens: number;
  estimatedCost: number;
  timestamp: string;
}

interface OpenAIBilling {
  totalCredit: number;
  usedCredit: number;
  availableCredit: number;
  lastUpdated: string;
}

class OpenAIService {
  private config: OpenAIConfig | null = null;
  private usageHistory: OpenAIUsage[] = [];

  constructor() {
    this.loadConfig().catch(console.error);
    this.loadUsageHistory();
  }

  // Helper to get user-friendly error messages
  private getUserFriendlyError(error: any): string {
    if (!error) return 'Error desconocido al conectar con OpenAI';

    // API Key issues
    if (error.message?.includes('API key')) {
      return 'La API Key de OpenAI no es válida. Por favor, verifica la configuración en Settings.';
    }

    // Rate limit errors
    if (error.status === 429 || error.message?.includes('rate limit')) {
      return 'Se ha excedido el límite de uso de OpenAI. Por favor, intenta de nuevo en unos minutos.';
    }

    // Insufficient quota/credits
    if (error.message?.includes('insufficient_quota') || error.message?.includes('quota')) {
      return 'Tu cuenta de OpenAI no tiene créditos suficientes. Por favor, revisa tu saldo en platform.openai.com';
    }

    // Network errors
    if (error.message?.includes('fetch') || error.message?.includes('network')) {
      return 'Error de conexión con OpenAI. Por favor, verifica tu conexión a internet.';
    }

    // Invalid request errors
    if (error.status === 400) {
      return 'La solicitud a OpenAI no es válida. Por favor, contacta soporte.';
    }

    // Server errors
    if (error.status >= 500) {
      return 'OpenAI está experimentando problemas temporales. Por favor, intenta de nuevo más tarde.';
    }

    return `Error al conectar con OpenAI: ${error.message || 'Error desconocido'}`;
  }

  private async loadConfig() {
    try {
      logger.debug('OpenAI loadConfig() iniciado...');

      // Use configService to get OpenAI config from database
      const { configService } = await import('./configService');

      // Force reload from database to get latest config
      await configService.loadConfig(true);
      const openaiConfig = await configService.getOpenAIConfigAsync();

      if (openaiConfig) {
        this.config = openaiConfig;
        logger.debug('OpenAI config loaded', {
          hasApiKey: !!this.config.apiKey,
          model: this.config.model
        });
      } else {
        logger.warn('OpenAI config not found in database');
        this.config = null;
      }
    } catch (error) {
      logger.error('Error loading OpenAI config', error);
      this.config = null;
    }
  }

  // Force reload configuration from database
  async reloadConfig(): Promise<boolean> {
    logger.debug('Forzando recarga de configuración de OpenAI...');
    this.config = null;
    await this.loadConfig();
    const isValid = this.isConfigured();
    logger.info('Configuración recargada. Válida', isValid);
    return isValid;
  }

  private loadUsageHistory() {
    try {
      const saved = localStorage.getItem('openai_usage_history');
      if (saved) {
        this.usageHistory = JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading usage history:', error);
      this.usageHistory = [];
    }
  }

  private saveUsageHistory() {
    try {
      // Keep only last 100 entries
      if (this.usageHistory.length > 100) {
        this.usageHistory = this.usageHistory.slice(-100);
      }
      localStorage.setItem('openai_usage_history', JSON.stringify(this.usageHistory));
    } catch (error) {
      console.error('Error saving usage history:', error);
    }
  }

  private calculateCost(model: string, promptTokens: number, completionTokens: number): number {
    const pricing = {
      'gpt-4': { prompt: 0.03, completion: 0.06 },
      'gpt-4-turbo': { prompt: 0.01, completion: 0.03 },
      'gpt-3.5-turbo': { prompt: 0.0015, completion: 0.002 }
    };

    const modelPricing = pricing[model] || pricing['gpt-4'];
    const promptCost = (promptTokens / 1000) * modelPricing.prompt;
    const completionCost = (completionTokens / 1000) * modelPricing.completion;
    
    return promptCost + completionCost;
  }

  private trackUsage(action: string, model: string, promptTokens: number, completionTokens: number) {
    const totalTokens = promptTokens + completionTokens;
    const estimatedCost = this.calculateCost(model, promptTokens, completionTokens);

    const usage: OpenAIUsage = {
      action,
      model,
      promptTokens,
      completionTokens,
      totalTokens,
      estimatedCost,
      timestamp: new Date().toISOString()
    };

    this.usageHistory.push(usage);
    this.saveUsageHistory();

    logger.debug(`OpenAI Usage: ${action} - ${totalTokens} tokens - $${estimatedCost.toFixed(4)}`);
  }

  // Check if OpenAI is configured. Only "the frontend has loaded default
  // settings (model/temperature/maxTokens) from configService" — the API key
  // is server-side (openai-proxy Edge Function reads OPENAI_API_KEY from
  // Deno env), so we deliberately do NOT gate on it here.
  isConfigured(): boolean {
    return !!this.config?.model;
  }

  // Retry logic with exponential backoff for transient errors
  private async retryWithExponentialBackoff<T>(
    fn: () => Promise<T>,
    operationName: string,
    maxRetries: number = 3
  ): Promise<T> {
    let lastError: any;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await fn();
      } catch (error: any) {
        lastError = error;

        // Don't retry on these errors
        const shouldNotRetry =
          error.message?.includes('API key') ||
          error.message?.includes('insufficient_quota') ||
          error.message?.includes('OpenAI not configured') ||
          (error.status && error.status >= 400 && error.status < 500 && error.status !== 429);

        if (shouldNotRetry) {
          logger.error(`${operationName} - Non-retryable error:`, error.message);
          const friendlyMessage = this.getUserFriendlyError(error);
          throw new Error(friendlyMessage);
        }

        // Retry on rate limit (429) or server errors (5xx)
        if (attempt < maxRetries) {
          const delay = Math.min(1000 * Math.pow(2, attempt), 10000); // Max 10 seconds
          logger.warn(`${operationName} - Attempt ${attempt + 1}/${maxRetries + 1} failed, retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries exhausted
    logger.error(`${operationName} - All ${maxRetries + 1} attempts failed`);
    const friendlyMessage = this.getUserFriendlyError(lastError);
    throw new Error(friendlyMessage);
  }

  async testConnection(): Promise<boolean> {
    try {
      logger.debug('Testing OpenAI connection via proxy...');
      const data = await callOpenAIProxy('test_connection');
      const ok = !!data?.data;
      if (ok) console.log('✅ OpenAI connection successful');
      return ok;
    } catch (error) {
      logger.error('OpenAI connection error', error);
      return false;
    }
  }

  async getBillingInfo(): Promise<OpenAIBilling | null> {
    // /v1/usage requires an sk-admin (organization) key, which we deliberately
    // don't expose through the proxy. Callers should treat null as "unknown".
    return null;
  }

  getUsageHistory(): OpenAIUsage[] {
    return this.usageHistory.slice().reverse(); // Most recent first
  }

  getTotalSpent(): number {
    return this.usageHistory.reduce((total, usage) => total + usage.estimatedCost, 0);
  }

  // Estimate cost based on approximate token count
  estimateCost(promptLength: number, expectedCompletionTokens: number = 200): number {
    if (!this.config) return 0;

    // Rough estimation: ~4 characters per token
    const estimatedPromptTokens = Math.ceil(promptLength / 4);
    return this.calculateCost(this.config.model, estimatedPromptTokens, expectedCompletionTokens);
  }

  async generateContactSummary(contactName: string, contact: ContactData, activities: ActivityData[]): Promise<string> {
    return this.retryWithExponentialBackoff(async () => {
      if (!this.config) {
        throw new Error('OpenAI not configured');
      }

      // Check abuse control before making API call
      // Optimized prompt to reduce tokens
      const recentActivity = activities.length > 0 ? activities[0]?.description : 'Sin actividad';
      const notesSummary = contact.notes ? contact.notes.substring(0, 150) : 'Sin notas';

      const prompt = `Resumen CRM:
Contacto: ${contact.fullName} (${contact.company})
Rol: ${contact.role || 'N/A'} | Estado: ${contact.leadStatus}
Interés: ${contact.productInterest}
Última actividad: ${recentActivity}
Notas: ${notesSummary}

Resume en 2-3 líneas los puntos clave para seguimiento comercial.`;

      const data = await callOpenAIProxy('chat_completion', {
        model: this.config!.model,
        messages: [
          { role: 'system', content: 'Eres un asistente especializado en CRM que genera resúmenes profesionales y concisos.' },
          { role: 'user', content: prompt },
        ],
        max_tokens: this.config!.maxTokens,
        temperature: this.config!.temperature,
      });

      const summary = data.choices[0]?.message?.content || 'No se pudo generar el resumen';

      this.trackUsage(
        `Resumen de ${contactName}`,
        this.config!.model,
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      return summary;
    }, 'generateContactSummary');
  }

  async generateResponseSuggestion(context: string, contactInfo: ContactData): Promise<string> {
    try {
      if (!this.config) {
        throw new Error('OpenAI not configured');
      }

      // Check abuse control before making API call
      // Optimized prompt for response suggestion
      const prompt = `Respuesta comercial:
Para: ${contactInfo.fullName} (${contactInfo.company})
Estado: ${contactInfo.leadStatus} | Interés: ${contactInfo.productInterest}
Contexto: ${context}

Genera email profesional y persuasivo (máx 150 palabras).`;

      const data = await callOpenAIProxy('chat_completion', {
        model: this.config!.model,
        messages: [
          { role: 'system', content: 'Eres un asistente comercial experto que genera respuestas profesionales y persuasivas para clientes potenciales.' },
          { role: 'user', content: prompt },
        ],
        max_tokens: this.config!.maxTokens,
        temperature: this.config!.temperature,
      });

      const suggestion = data.choices[0]?.message?.content || 'No se pudo generar la sugerencia';

      this.trackUsage(
        `Sugerencia para ${contactInfo.fullName}`,
        this.config!.model,
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      return suggestion;
    } catch (error) {
      console.error('Error generating response suggestion:', error);
      const friendlyMessage = this.getUserFriendlyError(error);
      throw new Error(friendlyMessage);
    }
  }

  async generateLeadAnalysis(lead: LeadData): Promise<string> {
    try {
      if (!this.config) {
        throw new Error('OpenAI not configured');
      }

      // Check abuse control before making API call
      // Parse product interest
      let productText = lead.productInterest;
      try {
        const products = JSON.parse(lead.productInterest);
        if (Array.isArray(products)) {
          productText = products.join(', ');
        }
      } catch {
        // Keep original string
      }

      // Optimized lead analysis prompt
      const notesSummary = lead.notes ? lead.notes.substring(0, 100) : 'Sin notas';
      const lastContact = lead.lastContactDate ? lead.lastContactDate.toLocaleDateString() : 'Nunca';
      const nextFollowUp = lead.nextFollowUp ? lead.nextFollowUp.toLocaleDateString() : 'Sin plan';

      const prompt = `Análisis Lead B2B:
Lead: ${lead.fullName} (${lead.company})
Fuente: ${lead.source} | Estado: ${lead.status} | Score: ${lead.score}/100
Interés: ${productText}
Último contacto: ${lastContact} | Próximo: ${nextFollowUp}
Notas: ${notesSummary}

Genera análisis de 3 líneas: potencial, riesgos/oportunidades, y recomendación de acción.`;

      const data = await callOpenAIProxy('chat_completion', {
        model: this.config!.model,
        messages: [
          { role: 'system', content: 'Eres un analista comercial experto especializado en evaluación de leads B2B. Generas análisis concisos y accionables.' },
          { role: 'user', content: prompt },
        ],
        max_tokens: this.config!.maxTokens,
        temperature: this.config!.temperature,
      });

      const analysis = data.choices[0]?.message?.content || 'No se pudo generar el análisis';

      this.trackUsage(
        `Análisis de lead ${lead.fullName}`,
        this.config!.model,
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      return analysis;
    } catch (error) {
      console.error('Error generating lead analysis:', error);
      const friendlyMessage = this.getUserFriendlyError(error);
      throw new Error(friendlyMessage);
    }
  }

  async analyzeLeadPriority(contactInfo: ContactData, formData: Record<string, any>): Promise<LeadPriorityAnalysis> {
    try {
      if (!this.config) {
        throw new Error('OpenAI not configured');
      }

      // Check abuse control before making API call
      const prompt = `Analiza la prioridad de este lead para seguimiento comercial:

Contacto: ${contactInfo.fullName}
Empresa: ${contactInfo.company}
Rol: ${contactInfo.role || 'No especificado'}
Estado actual: ${contactInfo.leadStatus}
Producto de interés: ${contactInfo.productInterest}
Notas: ${contactInfo.notes || 'Sin notas'}
Mensaje del formulario: ${formData.message || 'Sin mensaje'}

Responde SOLO con un JSON en este formato:
{
  "priority": "alta|media|baja",
  "reasoning": "Explicación breve de por qué tiene esta prioridad"
}`;

      const data = await callOpenAIProxy('chat_completion', {
        model: this.config!.model,
        messages: [
          { role: 'system', content: 'Eres un analista comercial experto. Responde SOLO con JSON válido.' },
          { role: 'user', content: prompt },
        ],
        max_tokens: 200,
        temperature: 0.3,
      });

      const content = data.choices[0]?.message?.content || '{}';

      this.trackUsage(
        `Análisis de ${contactInfo.fullName || 'contacto'}`,
        this.config!.model,
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      try {
        return JSON.parse(content);
      } catch {
        return {
          priority: 'medium',
          reasoning: 'No se pudo analizar automáticamente'
        };
      }
    } catch (error) {
      console.error('Error analyzing lead priority:', error);
      const friendlyMessage = this.getUserFriendlyError(error);
      return {
        priority: 'media',
        reasoning: `No se pudo analizar: ${friendlyMessage}`
      };
    }
  }

  async analyzeLeadForArchive(lead: LeadData, contextReasons: string): Promise<{ shouldArchive: boolean; reasoning: string } | null> {
    if (!this.isConfigured()) {
      console.warn('OpenAI not configured');
      return null;
    }

    const prompt = `Analiza si este lead debería ser archivado:

Lead:
- Nombre: ${lead.fullName}
- Empresa: ${lead.company}
- Email: ${lead.email}
- Estado: ${lead.status}
- Puntuación: ${lead.score}/100
- Producto de interés: ${lead.productInterest}
- Fuente: ${lead.source}
- Notas: ${lead.notes || 'Sin notas'}

Razones detectadas automáticamente:
${contextReasons}

Basándote en toda esta información, determina:
1. ¿Este lead debería ser archivado?
2. ¿Por qué? (proporciona un razonamiento breve)

Responde en formato JSON: {"shouldArchive": true/false, "reasoning": "explicación"}`;

    try {
      const data = await callOpenAIProxy('chat_completion', {
        model: this.config!.model,
        messages: [
          { role: 'system', content: 'Eres un experto en calificación de leads y CRM. Analiza leads para determinar si deberían archivarse.' },
          { role: 'user', content: prompt },
        ],
        temperature: 0.3,
        max_tokens: 300,
      });

      this.trackUsage(
        'lead_archive_analysis',
        data.model,
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      const content = data.choices[0]?.message?.content?.trim() || '{}';

      try {
        return JSON.parse(content);
      } catch {
        return null;
      }
    } catch (error) {
      console.error('Error analyzing lead for archive:', error);
      return null;
    }
  }

  async sendConversationalMessage(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    onChunk?: (chunk: string) => void,
    module?: string,
    tenantId?: string | null
  ): Promise<{ content: string; tokensUsed: number; model: string }> {
    return this.retryWithExponentialBackoff(async () => {
      if (!this.config) {
        throw new Error('OpenAI not configured');
      }

      // Check abuse control before making API call
      const enhancedMessages = await this.injectContextualPrompt(messages, module, tenantId);

      // Streaming is not forwarded through the proxy for now; the AI
      // Conversations panel that used onChunk is on the deprecation list.
      // If onChunk is provided we just deliver the full text in one call.
      const data = await callOpenAIProxy('chat_completion', {
        model: this.config!.model,
        messages: enhancedMessages,
        max_tokens: this.config!.maxTokens,
        temperature: this.config!.temperature,
      });

      const content = data.choices[0]?.message?.content || '';
      const tokensUsed = data.usage?.total_tokens || 0;

      this.trackUsage(
        'Conversación',
        this.config!.model,
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      if (onChunk && content) {
        onChunk(content);
      }

      return {
        content,
        tokensUsed,
        model: this.config!.model
      };
    }, 'sendConversationalMessage');
  }

  private async injectContextualPrompt(
    messages: Array<{ role: 'system' | 'user' | 'assistant'; content: string }>,
    module?: string,
    tenantId?: string | null
  ): Promise<Array<{ role: 'system' | 'user' | 'assistant'; content: string }>> {
    try {
      const { aiPromptsService } = await import('./aiPromptsService');

      const effectivePrompt = await aiPromptsService.getEffectivePrompt(
        module as any,
        tenantId ?? null
      );

      const hasSystemMessage = messages.some(msg => msg.role === 'system');

      if (hasSystemMessage) {
        return messages.map(msg => {
          if (msg.role === 'system') {
            return {
              ...msg,
              content: `${effectivePrompt.content}\n\n${msg.content}`
            };
          }
          return msg;
        });
      } else {
        return [
          {
            role: 'system',
            content: effectivePrompt.content
          },
          ...messages
        ];
      }
    } catch (error) {
      console.warn('⚠️ Failed to load contextual prompt, using messages as-is:', error);
      return messages;
    }
  }

  async analyzeLeadStage1(lead: LeadData): Promise<import('../types/leadIntelligence').Stage1Result> {
    return this.retryWithExponentialBackoff(async () => {
      if (!this.config) {
        throw new Error('OpenAI not configured');
      }


      const content = [
        lead.notes,
        lead.productInterest,
        Object.values(lead).filter(v => typeof v === 'string').join(' ')
      ].filter(Boolean).join('\n').substring(0, 600);

      const prompt = `Eres un filtro rápido de leads para una agencia de software. Tu única tarea es clasificar si este lead describe un problema de negocio real o no.

Lead:
- Nombre: ${lead.fullName}
- Empresa: ${lead.company}
- Interés: ${lead.productInterest}
- Mensaje/Notas: ${content}

Clasifica el lead y responde SOLO con JSON válido en este formato exacto:
{
  "result": "pass" | "reject",
  "category": "business_problem" | "idea_only" | "spam" | "unclear",
  "reason": "Una frase corta explicando la clasificación"
}

Reglas:
- "pass" solo si hay un problema de negocio claro y concreto.
- "reject" si es solo una idea vaga, spam, o el mensaje es incomprensible.`;

      const data = await callOpenAIProxy('chat_completion', {
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: 'Eres un clasificador de leads. Responde SOLO con JSON válido, sin texto adicional.' },
          { role: 'user', content: prompt },
        ],
        max_tokens: 150,
        temperature: 0.1,
        response_format: { type: 'json_object' },
      });

      const raw = data.choices[0]?.message?.content || '{}';

      this.trackUsage(
        `Stage1 filter: ${lead.fullName}`,
        'gpt-4o-mini',
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      try {
        const parsed = JSON.parse(raw);
        return {
          result: parsed.result === 'pass' ? 'pass' : 'reject',
          category: parsed.category || 'unclear',
          reason: parsed.reason || 'Sin razón proporcionada'
        };
      } catch {
        return { result: 'reject', category: 'unclear', reason: 'Error al parsear respuesta Stage 1' };
      }
    }, 'analyzeLeadStage1');
  }

  async analyzeLeadStage2(lead: LeadData): Promise<import('../types/leadIntelligence').Stage2Result> {
    return this.retryWithExponentialBackoff(async () => {
      if (!this.config) {
        throw new Error('OpenAI not configured');
      }


      const notesSummary = lead.notes ? lead.notes.substring(0, 400) : 'Sin notas';

      const prompt = `Eres un analista senior de negocio en una agencia de software especializada. Debes analizar en profundidad el siguiente lead y generar una evaluación estructurada.

## Lead
- Nombre: ${lead.fullName}
- Empresa: ${lead.company}
- Rol: ${lead.role || 'No especificado'}
- Email: ${lead.email}
- Fuente: ${lead.source}
- Interés declarado: ${lead.productInterest}
- Notas / Mensaje: ${notesSummary}

## Líneas de negocio disponibles
- A_CORE_SYSTEM: Sistemas de gestión core, CRMs, ERPs, plataformas internas de empresa.
- B_BIG_PLATFORM: Marketplaces, SaaS, plataformas complejas multi-usuario con modelo de negocio digital.
- C_RESCUE_IMPROVEMENTS: Rescates de proyectos existentes, mejoras, refactorizaciones, migraciones.

## Instrucciones de scoring (0-25 por dimensión, total 0-100)
- pain: ¿Qué tan claro y urgente es el dolor/problema de negocio?
- fit: ¿Encaja bien con nuestra oferta y capacidades?
- budget: ¿Hay señales de presupuesto real o capacidad de inversión?
- seriousness: ¿El lead tiene intención de compra real y no es exploratorio?

## Tiers
- HOT: score >= 70
- WARM: score 40-69
- COLD: score < 40

## Acciones recomendadas
- propose_phase0: Lead caliente, proponer una fase de descubrimiento de pago.
- propose_bounded_phase: Lead interesante, proponer una fase acotada (MVP, PoC).
- request_more_information: Potencial pero insuficiente información, pedir más contexto.
- reject_politely: No encaja con nuestra oferta o no hay oportunidad real.

Responde SOLO con JSON válido en este formato exacto:
{
  "projectLine": "A_CORE_SYSTEM" | "B_BIG_PLATFORM" | "C_RESCUE_IMPROVEMENTS",
  "leadScore": <número 0-100>,
  "leadScoreBreakdown": { "pain": <0-25>, "fit": <0-25>, "budget": <0-25>, "seriousness": <0-25> },
  "leadAnalysisSummary": "<párrafo narrativo de 2-4 frases>",
  "leadRedFlags": ["<flag1>", "<flag2>"],
  "leadPainSignals": ["<señal1>", "<señal2>"],
  "leadFitSignals": ["<señal1>", "<señal2>"],
  "recommendedAction": "propose_phase0" | "propose_bounded_phase" | "request_more_information" | "reject_politely",
  "painLevel": "low" | "medium" | "high" | "critical",
  "opportunityBand": "low" | "medium" | "high"
}`;

      const data = await callOpenAIProxy('chat_completion', {
        model: this.config!.model,
        messages: [
          { role: 'system', content: 'Eres un analista experto en evaluación de leads para agencias de software. Responde SOLO con JSON válido.' },
          { role: 'user', content: prompt },
        ],
        max_tokens: 800,
        temperature: 0.2,
        response_format: { type: 'json_object' },
      });

      const raw = data.choices[0]?.message?.content || '{}';

      this.trackUsage(
        `Stage2 analysis: ${lead.fullName}`,
        this.config!.model,
        data.usage?.prompt_tokens || 0,
        data.usage?.completion_tokens || 0
      );

      const parsed = JSON.parse(raw);

      const totalScore = parsed.leadScore ??
        ((parsed.leadScoreBreakdown?.pain || 0) +
          (parsed.leadScoreBreakdown?.fit || 0) +
          (parsed.leadScoreBreakdown?.budget || 0) +
          (parsed.leadScoreBreakdown?.seriousness || 0));

      const tier = totalScore >= 70 ? 'HOT' : totalScore >= 40 ? 'WARM' : 'COLD';

      return {
        projectLine: parsed.projectLine || 'A_CORE_SYSTEM',
        leadScore: totalScore,
        leadTier: tier,
        leadScoreBreakdown: {
          pain: parsed.leadScoreBreakdown?.pain || 0,
          fit: parsed.leadScoreBreakdown?.fit || 0,
          budget: parsed.leadScoreBreakdown?.budget || 0,
          seriousness: parsed.leadScoreBreakdown?.seriousness || 0
        },
        leadAnalysisSummary: parsed.leadAnalysisSummary || '',
        leadRedFlags: Array.isArray(parsed.leadRedFlags) ? parsed.leadRedFlags : [],
        leadPainSignals: Array.isArray(parsed.leadPainSignals) ? parsed.leadPainSignals : [],
        leadFitSignals: Array.isArray(parsed.leadFitSignals) ? parsed.leadFitSignals : [],
        recommendedAction: parsed.recommendedAction || 'request_more_information',
        painLevel: parsed.painLevel || 'medium',
        opportunityBand: parsed.opportunityBand || 'medium'
      };
    }, 'analyzeLeadStage2');
  }
}

export const openaiService = new OpenAIService();